/**
 * Hook for generating CV upload presigned URLs
 *
 * Uses TanStack Query mutation to POST file metadata to /recruitment/cv-upload-url
 * and receive a presigned S3 URL for direct upload.
 */

import { useMutation } from '@tanstack/react-query'
import { apiPost } from '@/lib/api/api-client'

/**
 * File metadata for presigned URL request
 */
export interface CvFileMetadata {
  mimeType: string
  extension: string
  originalFilename: string
  fileSize?: number
}

/**
 * Presigned URL response from API
 */
export interface PresignedUrlResponse {
  signedUrl: string
  key: string
  bucket: string
  expiresIn: number
}

/**
 * Mutation hook for generating CV upload presigned URLs
 *
 * @returns TanStack Query mutation object with:
 * - mutate: Function to trigger presigned URL generation
 * - mutateAsync: Async version that returns a promise
 * - data: Response data (PresignedUrlResponse | undefined)
 * - error: Error object if request fails
 * - isPending: Loading state
 * - isSuccess: Success state
 * - isError: Error state
 *
 * @example
 * ```tsx
 * const { mutateAsync, isPending, error } = useGenerateCvUploadUrl()
 *
 * const handleFileSelect = async (file: File) => {
 *   try {
 *     const response = await mutateAsync({
 *       mimeType: file.type,
 *       extension: file.name.split('.').pop() || '',
 *       originalFilename: file.name,
 *       fileSize: file.size
 *     })
 *     // Use response.signedUrl to upload file
 *   } catch (err) {
 *     // Handle error
 *   }
 * }
 * ```
 */
export const useGenerateCvUploadUrl = () => {
  return useMutation<PresignedUrlResponse, Error, CvFileMetadata>({
    mutationFn: async (fileMetadata: CvFileMetadata) => {
      return apiPost<PresignedUrlResponse>('/recruitment/cv-upload-url', fileMetadata)
    },
    // Retry configuration
    retry: (failureCount, error: Error) => {
      // Don't retry on 4xx errors (client errors - won't be fixed by retrying)
      const statusCode = (error as Error & { statusCode?: number })?.statusCode
      if (statusCode && statusCode >= 400 && statusCode < 500) {
        return false
      }
      // Retry up to 3 times for 5xx errors (server errors) or network errors
      return failureCount < 3
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
  })
}
